import {
    BookOpenIcon,
    BotIcon,
    CalendarDaysIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    ClockIcon,
    CompassIcon,
    DatabaseIcon,
    LayoutDashboardIcon,
    ListOrderedIcon,
    ShieldCheckIcon,
    SlidersHorizontalIcon,
    SparklesIcon,
    TableIcon,
    TagsIcon,
} from "lucide-react";
import {type ReactNode, useMemo, useState} from "react";

import {Badge} from "@components/ui/badge";
import {Card, CardContent} from "@components/ui/card";
import {useVNextSyncStatusAll} from "@hooks/use-vnext";

interface TabProps {
    workspaceId: string;
    scenarioId: string;
    actualMonths?: number;
    forecastMonths?: number;
}

interface SectionProps {
    icon: ReactNode;
    title: string;
    badge?: string;
    rightSlot?: ReactNode;
    children: ReactNode;
    defaultOpen?: boolean;
}

interface TabLinkProps {
    tab: string;
    children: ReactNode;
}

function Section({icon, title, badge, rightSlot, children, defaultOpen = false}: SectionProps) {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className="border rounded-lg bg-card">
            <button
                className="flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-muted/50 transition-colors"
                onClick={() => setOpen(!open)}
            >
                <span className="text-[#209DB3]">{icon}</span>
                <span className="font-semibold text-sm flex-1">{title}</span>
                {badge && (
                    <Badge variant="outline" className="text-[10px] mr-2">
                        {badge}
                    </Badge>
                )}
                {rightSlot}
                {open ? (
                    <ChevronDownIcon className="size-4 text-muted-foreground" />
                ) : (
                    <ChevronRightIcon className="size-4 text-muted-foreground" />
                )}
            </button>
            {open && <div className="px-4 pb-4 text-sm text-muted-foreground space-y-3">{children}</div>}
        </div>
    );
}

function openTab(tab: string) {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent("vnext-ui-action", {detail: {tab}}));
}

function TabLink({tab, children}: TabLinkProps) {
    return (
        <button
            type="button"
            className="text-[#209DB3] hover:underline font-medium"
            onClick={() => openTab(tab)}
        >
            {children}
        </button>
    );
}

function QuickStartCard() {
    const steps = useMemo(
        () => [
            "Data Sync tab: connect Xero + choose active tenant",
            "Data Sync tab: run step-by-step sync rows (accounts -> contacts -> transactions -> reports)",
            "Contact Mapping + Account Mapping: review AI-suggested mappings and bulk-approve",
            "Set period controls (MTD/QTD/YTD/Full Year/Full Month/Custom) + Compare + As of",
            "Upload files or folders in Knowledge (auto extraction runs)",
            "Use Approve as Context, Approve for Forecast, or Preview Impact on useful assumptions",
            "Review P&L, Cash Flow, Balance Sheet, and Modeling Workbench; inline edits create ops",
            "Verify in CEO Summary -> P&L -> Cash Flow (including 13-week forecast) -> AR",
        ],
        [],
    );

    return (
        <div className="rounded-lg border border-[#209DB3]/30 bg-[#209DB3]/5 p-4">
            <div className="flex items-center gap-2 mb-2">
                <CompassIcon className="size-4 text-[#209DB3]" />
                <p className="font-semibold text-foreground">Fast Start (10 minutes)</p>
            </div>
            <ol className="list-decimal list-inside space-y-1.5 text-sm">
                {steps.map((step) => (
                    <li key={step}>{step}</li>
                ))}
            </ol>
        </div>
    );
}

function formatRelativeTime(iso: string | null): string {
    if (!iso) return "—";
    const diffMs = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
}

function SyncStatusTag() {
    const {syncStatusAll, isLoading} = useVNextSyncStatusAll(false);

    const status = useMemo(() => {
        if (isLoading) {
            return {
                className: "bg-blue-100 text-blue-700 border-blue-200",
                text: "Checking sync...",
            };
        }
        if (!syncStatusAll) {
            return {
                className: "bg-gray-100 text-gray-600 border-gray-200",
                text: "Sync status unavailable",
            };
        }
        if (syncStatusAll.chain_running) {
            return {
                className: "bg-blue-100 text-blue-700 border-blue-200",
                text: "Sync in progress",
            };
        }

        const entities = Object.values(syncStatusAll.entities || {});
        const latestCompletedAt = entities
            .map((e) => e.completed_at)
            .filter((v): v is string => Boolean(v))
            .sort()
            .at(-1) ?? null;

        if (syncStatusAll.overall_health === "healthy") {
            return {
                className: "bg-green-100 text-green-700 border-green-200",
                text: `Synced as of ${formatRelativeTime(latestCompletedAt)}`,
            };
        }
        if (syncStatusAll.overall_health === "stale") {
            return {
                className: "bg-amber-100 text-amber-700 border-amber-200",
                text: `Stale sync (${formatRelativeTime(latestCompletedAt)})`,
            };
        }
        if (syncStatusAll.overall_health === "incomplete") {
            return {
                className: "bg-amber-100 text-amber-700 border-amber-200",
                text: "Sync incomplete",
            };
        }

        return {
            className: "bg-gray-100 text-gray-600 border-gray-200",
            text: "Not synced yet",
        };
    }, [isLoading, syncStatusAll]);

    return (
        <Badge variant="outline" className={`text-[10px] mr-2 ${status.className}`}>
            {status.text}
        </Badge>
    );
}

export function InstructionsTab(_props: TabProps) {
    return (
        <div className="flex-1 overflow-y-auto pb-8">
            <Card>
                <CardContent className="p-6 space-y-4">
                    <div className="space-y-1">
                        <h2 className="text-lg font-bold">Financial Model Guide</h2>
                        <p className="text-sm text-muted-foreground">
                            Workflow guide and best-practice usage. Expand sections as needed.
                        </p>
                    </div>

                    <QuickStartCard />

                    <Section
                        icon={<DatabaseIcon className="size-4" />}
                        title="1. Connect and Sync Data"
                        badge="Setup"
                        rightSlot={<SyncStatusTag />}
                        defaultOpen
                    >
                        <p>
                            Use the <TabLink tab="data_sync">Data Sync tab</TabLink> as your primary source setup screen.
                        </p>
                        <ol className="list-decimal list-inside space-y-1.5 ml-1">
                            <li>Connect Xero and confirm the active tenant.</li>
                            <li>Run the Data Sync steps in order, one row at a time.</li>
                            <li>Start with Chart of Accounts and Contacts, then sync transaction and report rows.</li>
                            <li>Use status badges (Synced, Stale, Incomplete, Error) to verify each step is complete.</li>
                            <li>If a snapshot-based tab shows stale or missing data, rebuild that snapshot from the tab that requests it, especially in <TabLink tab="cashflow_explorer">Cash Flow</TabLink>.</li>
                            <li>Proceed to <TabLink tab="contact_mapping">Contact Mapping</TabLink> and <TabLink tab="account_mapping">Account Mapping</TabLink> before relying on summaries and forecast views.</li>
                        </ol>
                        <p>
                            <strong>Important:</strong> avoid relying on one-shot sync prompts in chat for setup. Use the step rows in
                            {" "}
                            <TabLink tab="data_sync">Data Sync</TabLink>
                            {" "}
                            for a deterministic run.
                        </p>
                    </Section>

                    <Section
                        icon={<TagsIcon className="size-4" />}
                        title="2. Contact and Account Mapping"
                        badge="Setup"
                    >
                        <p>
                            Use
                            {" "}
                            <TabLink tab="contact_mapping">Contact Mapping</TabLink>
                            {" "}
                            and
                            {" "}
                            <TabLink tab="account_mapping">Account Mapping</TabLink>
                            {" "}
                            immediately after your first sync to classify customers, suppliers, and accounts correctly.
                        </p>
                        <ul className="list-disc list-inside space-y-1.5 ml-1">
                            <li>Run AI Auto-Map with guidelines when you want the model to follow your business rules.</li>
                            <li>Review AI-proposed items first; both tabs support bulk approval of proposed mappings.</li>
                            <li>Manually edit segments, parent contacts, parent groups, and account groups when the AI is close but not right.</li>
                            <li>Import CSV/Excel files to seed or update mappings in bulk.</li>
                        </ul>
                    </Section>

                    <Section
                        icon={<SlidersHorizontalIcon className="size-4" />}
                        title="3. Period, Baseline, and Display Controls"
                        badge="Display"
                    >
                        <ul className="list-disc list-inside space-y-1.5 ml-1">
                            <li><strong>Financial tabs:</strong> MTD, QTD, YTD, Full Year, Full Month, Custom, Compare, and As of.</li>
                            <li><strong>Forecast tabs:</strong> Baseline method plus Actuals and Forecast windows.</li>
                            <li>Baseline choices are 3-month avg, 6-month avg, 12-month avg, Last Month, YoY Flat, and YoY Growth.</li>
                            <li>Actuals windows run from 3 to 36 months; Forecast windows run from 3 to 24 months.</li>
                        </ul>
                    </Section>

                    <Section
                        icon={<LayoutDashboardIcon className="size-4" />}
                        title="4. Summary and CEO Summary"
                        badge="Overview"
                    >
                        <p>
                            Start in
                            {" "}
                            <TabLink tab="summary">Summary</TabLink>
                            {" "}
                            for freshness, checks, model activity, assumptions pipeline, and headline metrics.
                        </p>
                        <p>
                            Use
                            {" "}
                            <TabLink tab="ceo_summary">CEO Summary</TabLink>
                            {" "}
                            as the executive verification view after any sync or forecast change.
                        </p>
                        <ul className="list-disc list-inside space-y-1.5 ml-1">
                            <li>Expect KPI cards, executive snapshot, scorecard, trend view, review questions, and action plan.</li>
                            <li>Use the statement summaries alongside it when you want actuals and comparisons at a glance.</li>
                        </ul>
                    </Section>

                    <Section
                        icon={<TableIcon className="size-4" />}
                        title="5. Financial Statement Explorers"
                        badge="Core"
                    >
                        <ul className="list-disc list-inside space-y-1.5 ml-1">
                            <li><TabLink tab="cashflow_explorer">Cash Flow</TabLink>: 13-week rolling cash forecast — weekly view with opening/closing cash, inflows, outflows, overrides, and chain tie health.</li>
                            <li><TabLink tab="pnl_explorer">P&amp;L Explorer</TabLink> and <TabLink tab="bs">Balance Sheet</TabLink> are legacy tabs, replaced by the integrated 3-way planning engine in <TabLink tab="modeling_workbench">Modeling Workbench</TabLink>.</li>
                        </ul>
                    </Section>

                    <Section
                        icon={<SlidersHorizontalIcon className="size-4" />}
                        title="6. Modeling Workbench"
                        badge="Planning"
                    >
                        <p>
                            <TabLink tab="modeling_workbench">Modeling Workbench</TabLink>
                            {" "}
                            is the bottom-up planning surface for revenue and expense modeling.
                        </p>
                        <ul className="list-disc list-inside space-y-1.5 ml-1">
                            <li>Use revenue assumptions by customer, parent, or segment to drive the same 3-statement engine shown below.</li>
                            <li>Create and reuse revenue templates, bulk-apply them, and manage assumptions in one place.</li>
                            <li>Switch between P&amp;L, Balance Sheet, and Cash Flow inside the workbench, then review the assumptions register and op history.</li>
                        </ul>
                    </Section>

                    <Section
                        icon={<CalendarDaysIcon className="size-4" />}
                        title="7. 13-Week Rolling Cash Forecast"
                        badge="Cash"
                    >
                        <p>
                            Open
                            {" "}
                            <TabLink tab="cashflow_explorer">Cash Flow</TabLink>
                            {" "}
                            for the weekly short-term cash view.
                        </p>
                        <ul className="list-disc list-inside space-y-1.5 ml-1">
                            <li>It tracks opening cash, inflows, outflows, net cash flow, closing cash, and chain tie health.</li>
                            <li>Row groups separate receipts, cost of sales and payroll, operating expenses, investing, and financing/tax.</li>
                            <li>Use AR roll-forward, concentration risk, collections curve, and confidence tags before changing weekly assumptions.</li>
                            <li>Manual overrides are supported on forecast cells and remain in place until cleared.</li>
                        </ul>
                    </Section>

                    <Section
                        icon={<ClockIcon className="size-4" />}
                        title="8. Accounts Receivable"
                        badge="Analysis"
                    >
                        <p>
                            Use
                            {" "}
                            <TabLink tab="ar">AR</TabLink>
                            {" "}
                            when cash risk is driven by collections, overdue debtors, or customer concentration.
                        </p>
                        <ul className="list-disc list-inside space-y-1.5 ml-1">
                            <li>Review DSO, collection rate, overdue percentage, invoice counts, and average invoice value.</li>
                            <li>Use the aging buckets and collection trend to spot pressure early.</li>
                            <li>Check HHI and top-customer concentration before you assume AR is diversified.</li>
                        </ul>
                    </Section>

                    <Section
                        icon={<ListOrderedIcon className="size-4" />}
                        title="9. Scenarios and Ops History"
                        badge="Operations"
                    >
                        <ul className="list-disc list-inside space-y-1.5 ml-1">
                            <li>If your workspace has multiple scenarios, use the Scenario selector to switch or create one.</li>
                            <li>Inline edits in the explorer grids and workbench create operations automatically.</li>
                            <li>
                                Use
                                {" "}
                                <TabLink tab="ops">Ops</TabLink>
                                {" "}
                                to filter active ops vs revisions, separate agent vs human changes, undo single ops, and purge superseded revisions.
                            </li>
                        </ul>
                    </Section>

                    <Section
                        icon={<BookOpenIcon className="size-4" />}
                        title="10. Knowledge"
                        badge="Context"
                    >
                        <ol className="list-decimal list-inside space-y-1.5 ml-1">
                            <li>
                                Upload files or folders in
                                {" "}
                                <TabLink tab="knowledge">Knowledge</TabLink>
                                {" "}
                                (PDF, DOCX, XLSX, CSV, TXT, MD, HTML).
                            </li>
                            <li>Extraction runs automatically after upload.</li>
                            <li>Use <strong>Approve as Context</strong>, <strong>Approve for Forecast</strong>, <strong>Apply</strong>, and <strong>Preview Impact</strong> to control what reaches the model.</li>
                        </ol>
                    </Section>

                    <Section
                        icon={<SparklesIcon className="size-4" />}
                        title="11. Sandbox for Custom Analysis"
                        badge="Experimental"
                    >
                        <p>
                            Sandbox is for custom artifacts: analysis notes, tables, and charts built from mixed inputs.
                        </p>
                        <ul className="list-disc list-inside space-y-1.5 ml-1">
                            <li>Use Sandbox when client-specific logic does not fit the standard statement tabs.</li>
                            <li>Combine model snapshots, knowledge documents, uploads, and other data sources into one analysis run.</li>
                            <li>Promote validated sandbox findings back into the model when they should become operations.</li>
                        </ul>
                        <p>
                            Open
                            {" "}
                            <TabLink tab="sandbox">Sandbox</TabLink>
                            {" "}
                            to run ad-hoc analysis artifacts.
                        </p>
                    </Section>

                    <Section
                        icon={<ShieldCheckIcon className="size-4" />}
                        title="12. Financial Check Discipline"
                        badge="Accuracy"
                    >
                        <p>
                            Treat checks as internal controls. Do not edit the model until you understand the variance.
                        </p>
                        <ul className="list-disc list-inside space-y-1.5 ml-1">
                            <li>P&amp;L totals should reconcile to Xero report figures.</li>
                            <li>Cash Flow should tie opening and closing cash correctly across weekly and monthly views.</li>
                            <li>Balance Sheet should hold its balance check and align with Xero check figures where provided.</li>
                            <li>If snapshot completeness warnings appear, fix the data issue before relying on the output.</li>
                        </ul>
                    </Section>

                    <Section
                        icon={<BotIcon className="size-4" />}
                        title="13. Agent Prompt Patterns That Work"
                        badge="Agent"
                    >
                        <div className="space-y-2 mt-2">
                            <div>
                                <p className="font-medium text-foreground">Cash forecasting</p>
                                <ul className="list-disc list-inside ml-2 space-y-0.5">
                                    <li><em>"Summarize the 13-week cash forecast and call out the biggest runway risks."</em></li>
                                    <li><em>"Override week 5 customer receipts to $45,000 and show the impact on closing cash."</em></li>
                                </ul>
                            </div>
                            <div>
                                <p className="font-medium text-foreground">Mapping</p>
                                <ul className="list-disc list-inside ml-2 space-y-0.5">
                                    <li><em>"Review proposed contact mappings and tell me what still needs manual cleanup."</em></li>
                                    <li><em>"Assign marketing accounts to a Sales &amp; Marketing parent group and summarise the changes."</em></li>
                                </ul>
                            </div>
                            <div>
                                <p className="font-medium text-foreground">AR and planning</p>
                                <ul className="list-disc list-inside ml-2 space-y-0.5">
                                    <li><em>"Show the AR aging breakdown and the customers with the highest overdue balances."</em></li>
                                    <li><em>"Create a conservative revenue template in Modeling Workbench and apply it to the enterprise segment."</em></li>
                                </ul>
                            </div>
                            <div>
                                <p className="font-medium text-foreground">Knowledge</p>
                                <ul className="list-disc list-inside ml-2 space-y-0.5">
                                    <li><em>"Extract assumptions from the latest upload and preview their forecast impact."</em></li>
                                    <li><em>"Use the uploaded board notes as context for the executive summary."</em></li>
                                </ul>
                            </div>
                        </div>
                        <p>
                            If you want deep transaction drilldown, jump to
                            {" "}
                            <TabLink tab="transactions">Transactions</TabLink>
                            {" "}
                            and open records directly from provenance links.
                        </p>
                    </Section>
                </CardContent>
            </Card>
        </div>
    );
}
